<div class="medical-service-formgroup-setup-container">
  <!-- Header -->
  <div class="setup-header">
    <h2>Medical Service - Form Group Assignments</h2>
    <p class="subtitle">Assign form groups to medical services. When a medical service is provided to a patient, forms in the assigned group will be sent automatically.</p>
  </div>

  <!-- Filters -->
  <div class="filters-section">
    <div class="filter-row">
      <div class="search-box">
        <input
          type="text"
          [(ngModel)]="filterText"
          (ngModelChange)="onFilterChange()"
          placeholder="Search medical services..."
          class="form-control"
        />
      </div>
      <div class="checkbox-filter">
        <label>
          <input
            type="checkbox"
            [(ngModel)]="showOnlyWithForms"
            (ngModelChange)="onFilterChange()"
          />
          Show only services with forms assigned
        </label>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <div class="spinner"></div>
    <p>Loading...</p>
  </div>

  <!-- Main Table -->
  <div *ngIf="!isLoading && ViewPermission" class="table-container">
    <table class="data-table">
      <thead>
        <tr>
          <th>Medical Service</th>
          <th>Description</th>
          <th>Assigned Form Group</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let service of paginatedMedicalServices">
          <td class="service-name">{{ service.serviceName }}</td>
          <td class="service-desc">{{ service.serviceDescription || '-' }}</td>
          <td class="group-name">
            <span *ngIf="service.hasFormGroup" class="badge badge-success">
              {{ service.groupName }}
            </span>
            <span *ngIf="!service.hasFormGroup" class="badge badge-empty">
              No forms assigned
            </span>
          </td>
          <td class="status">
            <span
              *ngIf="service.hasFormGroup"
              class="status-indicator active"
              title="Forms will be sent when this medical service is provided"
            >
              ●
            </span>
            <span
              *ngIf="!service.hasFormGroup"
              class="status-indicator inactive"
              title="No forms configured"
            >
              ○
            </span>
          </td>
          <td class="actions">
            <button *ngIf="InsertPermission"
              class="btn btn-primary btn-sm"
              (click)="openAssignModal(service)"
              title="{{ service.hasFormGroup ? 'Change form group' : 'Assign form group' }}"
            >
              {{ service.hasFormGroup ? 'Change' : 'Assign' }}
            </button>
            <button
              *ngIf="service.hasFormGroup && DeletePermission"
              class="btn btn-danger btn-sm"
              (click)="openDeleteModal(service)"
              title="Remove form group assignment"
            >
              Remove
            </button>
          </td>
        </tr>
        <tr *ngIf="paginatedMedicalServices.length === 0">
          <td colspan="5" class="text-center no-data">
            <p *ngIf="!filterText && !showOnlyWithForms">No medical services found</p>
            <p *ngIf="filterText || showOnlyWithForms">No medical services match your filters</p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  <div *ngIf="!isLoading && totalPages > 1" class="pagination">
    <button
      class="btn btn-secondary"
      [disabled]="currentPage === 1"
      (click)="previousPage()"
    >
      Previous
    </button>
    <span class="page-info">
      Page {{ currentPage }} of {{ totalPages }}
      ({{ filteredMedicalServices.length }} total)
    </span>
    <button
      class="btn btn-secondary"
      [disabled]="currentPage === totalPages"
      (click)="nextPage()"
    >
      Next
    </button>
  </div>

  <!-- Assign/Change Modal -->
  <div *ngIf="showAssignModal" class="modal-overlay" (click)="closeAssignModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>{{ currentMedicalService?.hasFormGroup ? 'Change' : 'Assign' }} Form Group</h3>
        <button class="close-btn" (click)="closeAssignModal()">&times;</button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label for="medicalServiceName">Medical Service:</label>
          <input
            type="text"
            id="medicalServiceName"
            [value]="currentMedicalService?.serviceName"
            readonly
            class="form-control readonly"
          />
        </div>

        <div class="form-group">
          <label for="formGroupSelect">Form Group: *</label>
          <select
            id="formGroupSelect"
            [(ngModel)]="selectedGroupName"
            class="form-control"
            [disabled]="!hasFormGroups"
          >
            <option value="">-- Select a form group --</option>
            <option *ngFor="let group of formGroups" [value]="group">
              {{ group }}
            </option>
          </select>
          <small *ngIf="!hasFormGroups" class="text-danger">
            No form groups available. Please create form groups first in the Form Group Setup page.
          </small>
        </div>

        <!-- Policy Selection -->
        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              [(ngModel)]="selectedRequireForEachService"
            />
            <span>Require forms for each service</span>
          </label>
          <small class="help-text">
            <strong>If checked:</strong> Forms will be sent every time this medical service is provided, even if the patient has completed them before.
            <br>
            <strong>If unchecked:</strong> Forms will only be sent if the patient has never completed them.
          </small>
        </div>

        <div class="info-box">
          <strong>📋 When to check "Require for each service":</strong>
          <ul>
            <li>Pre-procedure questionnaires (needs current info)</li>
            <li>Service-specific intake forms</li>
            <li>Time-sensitive health assessments</li>
            <li>Consent forms requiring recent patient signatures</li>
          </ul>
          <strong>When to leave unchecked:</strong>
          <ul>
            <li>HIPAA consent (one-time only)</li>
            <li>General consent forms</li>
            <li>Policies (return policy, no-show, etc.)</li>
            <li>Medical history forms (completed once)</li>
          </ul>
        </div>
      </div>
      <div class="modal-footer">
        <button
          class="btn btn-secondary"
          (click)="closeAssignModal()"
          [disabled]="isSaving"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          (click)="saveMapping()"
          [disabled]="isSaving || !selectedGroupName || !hasFormGroups"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div *ngIf="showDeleteModal" class="modal-overlay" (click)="closeDeleteModal()">
    <div class="modal-content modal-sm" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Remove Form Group Assignment</h3>
        <button class="close-btn" (click)="closeDeleteModal()">&times;</button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to remove the form group assignment from:</p>
        <p class="text-bold">{{ currentMedicalService?.serviceName }}</p>
        <p class="text-muted">
          Current assignment: <span class="badge badge-warning">{{ currentMedicalService?.groupName }}</span>
        </p>
        <div class="warning-box">
          ⚠️ After removal, no forms will be sent when this medical service is provided.
        </div>
      </div>
      <div class="modal-footer">
        <button
          class="btn btn-secondary"
          (click)="closeDeleteModal()"
          [disabled]="isSaving"
        >
          Cancel
        </button>
        <button
          class="btn btn-danger"
          (click)="confirmDelete()"
          [disabled]="isSaving"
        >
          {{ isSaving ? 'Removing...' : 'Remove' }}
        </button>
      </div>
    </div>
  </div>
</div>
